import FingerprintJS from '@fingerprintjs/fingerprintjs'
import type { InternalAxiosRequestConfig } from 'axios'

/**
 * Resolves and memoises the FingerprintJS visitorId for this browser. The agent
 * loads on first call and the resolved id is cached, so every request after the
 * first reuses it without recomputing. Resolves to null if fingerprinting fails,
 * so callers can omit the header rather than break the request.
 *
 * The fingerprint is recomputed from real device/browser characteristics rather
 * than read from storage, so a session replayed from a different device produces
 * a different id — which is what lets the backend session-risk scorer detect it.
 */
let deviceIdPromise: Promise<string | null> | null = null

export function getDeviceId(): Promise<string | null> {
    if (deviceIdPromise === null) {
        deviceIdPromise = FingerprintJS.load()
            .then((agent) => agent.get())
            .then((result) => result.visitorId)
            .catch(() => null)
    }

    return deviceIdPromise
}

/**
 * Axios request interceptor that stamps the device fingerprint onto every
 * request as `X-Device-Id`, which `BaseRequest` reads server-side as the
 * `deviceId`. Omits the header (rather than failing the request) when the
 * fingerprint cannot be resolved.
 */
export async function attachDeviceIdHeader<T extends InternalAxiosRequestConfig>(
    config: T,
): Promise<T> {
    const deviceId = await getDeviceId()

    if (deviceId) {
        config.headers.set('X-Device-Id', deviceId)
    }

    return config
}
